import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import { PlayerNamesService } from '../../../core/services/player-names.service';
import { PlayerView } from '../../../core/types/dashboard.types';
import { User } from '../../../core/types/auth.types';

/**
 * BE-767/BE-768: тип сущности в общем роуте admin/users/:type/:id.
 * `player` — игрок; `business`/`team` — auth-аккаунт (бизнес-аккаунт / команда).
 */
export type UserCardType = 'player' | 'business' | 'team';

/** BE-767/BE-768: результат резолвера карточки пользователя. */
export interface UserCardResolved {
  /** Ветка карточки из :type. */
  type: UserCardType | string;
  /** Игрок (для ветки player); null, если не найден или ветка иная. */
  player: PlayerView | null;
  /** Auth-аккаунт (для веток business/team); null, если не найден или ветка иная. */
  authUser: User | null;
  /** true — ветка распознана и поддержана (player / business / team). */
  supported: boolean;
}

const EMPTY = (type: string, supported = false): UserCardResolved => ({
  type,
  player: null,
  authUser: null,
  supported,
});

/**
 * BE-767/BE-768: резолвер общего роута admin/users/:type/:id.
 * Ветки: `player` (GET /players/:id), `business`/`team` (GET /auth/users → find by id).
 * Отдельного GET-by-id для auth-аккаунта нет — берём список и находим по id.
 * Ошибки не блокируют навигацию — компонент отрисует состояние error/empty.
 */
export const userCardResolver: ResolveFn<UserCardResolved> = (route): Observable<UserCardResolved> => {
  const apiService = inject(ApiService);
  const playerNames = inject(PlayerNamesService);
  const type = route.paramMap.get('type') ?? '';
  const id = route.paramMap.get('id') ?? '';

  if (!id) {
    return of(EMPTY(type));
  }

  if (type === 'player') {
    // Карточку всегда берём свежей — кэш имён её не подменяет. Но раз игрок уже
    // получен, BE-1154: кладём его имя в общий справочник, чтобы соседние экраны
    // (реф-структура, победители розыгрыша) не запрашивали его повторно.
    return apiService.getPlayerById(id).pipe(
      map((player) => {
        playerNames.prime(player);
        return { type, player, authUser: null, supported: true } as UserCardResolved;
      }),
      catchError(() => of({ ...EMPTY(type, true) })),
    );
  }

  if (type === 'business' || type === 'team') {
    return apiService.getUsers().pipe(
      map((response) => {
        const authUser = (response.users ?? []).find((u) => u.id === id) ?? null;
        return { type, player: null, authUser, supported: true } as UserCardResolved;
      }),
      catchError(() => of({ ...EMPTY(type, true) })),
    );
  }

  return of(EMPTY(type));
};
